<template>
  <div class="loan-info-card">
    <!-- 标题 -->
    <h2 class="section-title">贷款申请信息</h2>

    <!-- 贷款基本信息 -->
    <div class="basic-info-grid">
      <!-- 贷款产品名称 -->
      <div class="info-item">
        <div class="info-label">贷款产品名称</div>
        <div class="info-value product-name">{{ loanInfo.productName }}</div>
      </div>
      <!-- 申请金额 -->
      <div class="info-item">
        <div class="info-label">申请金额</div>
        <div class="info-value amount">{{ loanInfo.amount }}</div>
      </div>
      <!-- 贷款期限 -->
      <div class="info-item">
        <div class="info-label">贷款期限</div>
        <div class="info-value">{{ loanInfo.term }}</div>
      </div>
      <!-- 经营年限 -->
      <div class="info-item">
        <div class="info-label">经营年限</div>
        <div class="info-value">{{ loanInfo.operatingYears }}</div>
      </div>
      <!-- 年营业额 -->
      <div class="info-item">
        <div class="info-label">年营业额</div>
        <div class="info-value">{{ loanInfo.annualTurnover }}</div>
      </div>
      <!-- 贷款用途 -->
      <div class="info-item">
        <div class="info-label">贷款用途</div>
        <div class="info-value">{{ loanInfo.purpose }}</div>
      </div>
    </div>

    <!-- 用途说明 -->
    <div class="business-description">
      <div class="info-label">用途说明</div>
      <div class="description-content">
        {{ loanInfo.businessDescription }}
      </div>
    </div>

    <!-- 附件信息 -->
    <div class="attachments">
      <div class="info-label">附件</div>
      <div class="attachment-files">
        <!-- 有附件时显示附件列表 -->
        <template v-if="loanInfo.attachments && loanInfo.attachments.length > 0">
          <el-link
            v-for="(file, index) in loanInfo.attachments"
            :key="index"
            type="primary"
            class="file-link"
            @click="handleFileClick(file)"
          >
            <i class="el-icon-document"></i>
            {{ file.name }}
          </el-link>
        </template>
        <!-- 无附件时显示提示文字 -->
        <div v-else class="no-attachments">
          暂无附件
        </div>
      </div>
    </div>
    <!-- 文件预览对话框 -->
    <el-dialog :title="previewDialogTitle" :visible.sync="previewDialogVisible" width="1000px">
      <div class="preview-container">
        <!-- PDF预览 -->
        <div v-if="previewType === 'pdf'" class="pdf-preview-container">
          <PdfComponent :pdfUrl="previewUrl" />
        </div>
        <!-- 图片预览 -->
        <div v-else-if="previewType === 'image'" class="image-preview-container">
          <el-image 
            :src="previewUrl" 
            fit="contain"
            style="width: 100%; height: 600px;"
          ></el-image>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PdfComponent from '@/components/pdf/PdfComponent.vue'

export default {
  name: 'LoanInfoCard',
  components: {
    PdfComponent
  },
  props: {
    loanInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      photoBaseUrl: window.globalUrl.BASE_API + 'admin/file/get?ossFilePath=',
      previewDialogTitle: '',
      previewDialogVisible: false,
      previewUrl: '/admin/file/cqjy/financeAdd/3d25e09ac9d84ed986d06a75c3b727a3.pdf',
      previewType: '', // 'pdf' 或 'image'
    }
  },
  methods: {
    // 处理附件点击
    handleFileClick(file) {
      const fileName = file.name.toLowerCase()
      
      // 判断文件类型
      if (fileName.endsWith('.pdf')) {
        // PDF文件处理
        this.previewUrl = file.url
        this.previewDialogTitle = 'PDF预览：' + file.name
        this.previewType = 'pdf'
        this.previewDialogVisible = true
      } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || 
                 fileName.endsWith('.png') || fileName.endsWith('.gif') || 
                 fileName.endsWith('.bmp') || fileName.endsWith('.webp')) {
        // 图片文件处理
        this.previewUrl = this.photoBaseUrl + file.url
        this.previewDialogTitle = '图片预览：' + file.name
        this.previewType = 'image'
        this.previewDialogVisible = true
      } else {
        // 其他文件类型，直接下载或打开新窗口
        // window.open(file.url, '_blank')
        this.$message.warning("不支持的文件类型，无法预览")
      }
    }
  }
}
</script>

<style lang="less" scoped>
// 变量定义 - 从设计稿提取
@primary-color: #2563eb; // 主色调
@success-color: #16a34a; // 成功色（金额）
@text-primary: #1f2937; // 主文本色
@text-secondary: #6b7280; // 次要文本色
@white: #ffffff; // 白色背景
@border-radius: 8px; // 圆角
@box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05); // 阴影

.loan-info-card {
  background-color: @white;
  border-radius: @border-radius;
  box-shadow: @box-shadow;
  padding: 24px;
  margin-bottom: 24px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    line-height: 28px;
    margin: 0 0 24px 0;
  }

  // 基本信息网格布局
  .basic-info-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    margin-bottom: 24px;

    .info-item {
      .info-label {
        font-size: 14px;
        color: @text-secondary;
        line-height: 20px;
        margin-bottom: 4px;
      }

      .info-value {
        font-size: 16px;
        font-weight: 500;
        color: @text-primary;
        line-height: 24px;

        &.product-name {
          color: @primary-color;
          font-size: 18px;
          font-weight: 600;
        }

        &.amount {
          color: @success-color;
          font-size: 24px;
          font-weight: 700;
        }
      }
    }
  }

  // 经营情况说明
  .business-description {
    margin-bottom: 24px;

    .info-label {
      font-size: 14px;
      color: @text-secondary;
      line-height: 20px;
      margin-bottom: 8px;
    }

    .description-content {
      font-size: 16px;
      color: @text-primary;
      line-height: 24px;
      background-color: rgba(0, 0, 0, 0.02);
      padding: 12px;
      border-radius: 4px;
      border-left: 3px solid @primary-color;
    }
  }

  // 附件信息
  .attachments {
    .info-label {
      font-size: 14px;
      color: @text-secondary;
      line-height: 20px;
      margin-bottom: 12px;
    }

    .attachment-files {
      display: flex;
      gap: 24px;
      flex-wrap: wrap;

      .file-link {
        font-size: 16px;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        background-color: rgba(37, 99, 235, 0.05);
        border-radius: 4px;
        transition: all 0.3s ease;

        &:hover {
          background-color: rgba(37, 99, 235, 0.1);
          text-decoration: underline;
        }

        i {
          font-size: 18px;
        }
      }

      // 暂无附件样式
      .no-attachments {
        font-size: 14px;
        color: @text-secondary;
        padding: 12px;
        background-color: rgba(0, 0, 0, 0.02);
        border-radius: 4px;
        border: 1px dashed #d9d9d9;
        text-align: center;
        width: 100%;
      }
    }
  }
}

// 响应式设计
@media (max-width: 1024px) {
  .loan-info-card {
    padding: 16px;

    .basic-info-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

@media (max-width: 768px) {
  .loan-info-card {
    .basic-info-grid {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    .attachment-files {
      flex-direction: column;
      gap: 12px;

      .file-link {
        justify-content: center;
      }
    }
  }
}

// 统一预览容器样式
.preview-container {
  width: 100%;
}

// PDF预览容器样式
.pdf-preview-container {
  height: 600px; // 固定高度，确保PDF组件有足够空间
  width: 100%;
  overflow: hidden;
}

// 图片预览容器样式
.image-preview-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  height: 600px;
}
</style>
